/**
 * PHARMAKON — Axios API Client Singleton
 * All API services import this — never create axios instances elsewhere.
 */
import axios from "axios";
import { API_BASE_URL } from "../config/app.config.js";
import mockAdapter from "../mock/adapter.js";
import { isBetaMode } from "../mock/beta-mode.js";
// Module 09 — the offline gate wraps the network adapter (same seam as the
// beta mock). Online + non-enrolled terminals: byte-identical pass-through.
import { makeOfflineGate } from "../offline/offlineGate.js";
// X XWP-72 — the error codes of the team workstation (one list, parity-tested against the server).
import { CODES_SESSION_EQUIPE } from "../config/session-equipe.config.js";

// Resolve the real network adapter ONCE. In axios 1.x `axios.defaults.adapter`
// is NOT a function — it's an array of adapter names (["xhr","http","fetch"]).
// `axios.getAdapter()` turns that into the concrete adapter function for the
// current environment. Calling the raw array would throw
// "defaultAdapter is not a function". When BETA mode is ON we route to the
// in-memory mock backend instead; otherwise we forward to the network adapter
// through the Module-09 offline gate.
const networkAdapter = axios.getAdapter(axios.defaults.adapter);
const offlineGate = makeOfflineGate(networkAdapter);

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15_000,
  headers: { "Content-Type": "application/json" },
  adapter: (config) =>
    isBetaMode() ? mockAdapter(config) : offlineGate(config),
});
/**
 * The request interceptor, exported for unit tests. X XWP-72 — a call of the team workstation names
 * its team token in the request config (`jetonEquipe`, services/session-equipe.api.js): it wins over
 * the user's token, so the picker never sends a user's JWT and a business call never the team token.
 */
export function onRequest(config) {
  const token = config.jetonEquipe ?? localStorage.getItem("pharmakon_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // a request sent by a user picked by PIN stays theirs when its answer comes back after the
  // selection was forgotten (a concurrent 401 already returned to the picker): never /auth/refresh
  if (!config.jetonEquipe && sessionEquipe?.active?.()) config._selectionEquipe = true;
  return config;
}
apiClient.interceptors.request.use(onRequest);
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

/**
 * Notify the in-app toast layer that the backend refused a request because
 * the pharmacy's licence is missing a paid module. The matching response
 * shape comes from server/src/middleware/licence.js → requireModule():
 *
 *   { success: false, code: "FORBIDDEN", message: "...", details: { module } }
 *
 * Module-level code can't reach into the React ToastContext directly, so
 * we dispatch a CustomEvent that ToastProvider listens for. We deliberately
 * do NOT redirect here — the per-page <ModuleGate> already renders the
 * locked empty state, and a hard redirect would steal context the user
 * needs (e.g. they were mid-form). See components/billing/ModuleGate.jsx.
 */
function emitModuleForbiddenToast(moduleCode) {
  try {
    window.dispatchEvent(
      new CustomEvent("pharmakon:toast", {
        detail: {
          message: "Module non activé",
          type: "warning",
          // tag with module so future listeners (e.g. analytics) can scope
          module: moduleCode,
        },
      }),
    );
  } catch {
    // window or CustomEvent unavailable (SSR, very old engine) — silent.
  }
}

/**
 * Auth endpoints whose 401 is a CREDENTIAL OUTCOME, not an expired session.
 * Their rejection must reach the calling form so it can render the inline
 * error (LoginPage/authStore already do). Without this exclusion the
 * interceptor's kick-to-login branch fired on the login request's own 401
 * (no refresh token in the logged-out state) and hard-reloaded /login,
 * wiping the typed e-mail/password and the error state before it could
 * paint (R3-H27/R3-H28-family). Config-driven — extend here, never
 * special-case in components.
 */
export const AUTH_PASSTHROUGH_PATHS = Object.freeze([
  "/auth/login",
  "/auth/refresh",
  "/auth/logout",
  "/auth/forgot-password",
  "/auth/reset-password",
  "/auth/register",
  "/auth/register-fournisseur",
]);

/**
 * 401 codes that are a CREDENTIAL OUTCOME on an authenticated request — the
 * session is valid, the password re-typed to confirm a sensitive action is not
 * (« Corriger le stock » « Votre mot de passe », changing one's own password).
 * The server's expired / invalid session answers carry TOKEN_INVALID /
 * TOKEN_EXPIRED, never these: a refresh-and-retry would only re-send the wrong
 * password (and a failed refresh would log the user out of a typo).
 */
export const CREDENTIAL_OUTCOME_CODES = Object.freeze(["INVALID_CREDENTIALS"]);

/** True when a 401 body is a credential outcome (above), not an expired session. */
export function isCredentialOutcome(body) {
  return Boolean(body && CREDENTIAL_OUTCOME_CODES.includes(body.code));
}

/**
 * X XWP-72 — every answer of the team login and of the workstation (`/auth/equipe/*`: the login steps,
 * the renewal, the picker, the PIN switch, a picked user's prolongation and step-up) reaches its caller
 * — the screens and lib/session-equipe.js decide; never a `/auth/refresh`, never a jump to /login.
 */
export const AUTH_EQUIPE_PREFIX = "/auth/equipe/";

/** True when the failing request targets an auth endpoint listed above. */
export function isAuthPassthroughRequest(url) {
  if (!url) return false;
  // `url` may be relative to baseURL ("/auth/login") or absolute.
  const path = String(url).replace(API_BASE_URL, "");
  return (
    path.startsWith(AUTH_EQUIPE_PREFIX) ||
    AUTH_PASSTHROUGH_PATHS.some((p) => path === p || path.startsWith(`${p}?`))
  );
}

/**
 * X XWP-72 — the team workstation plugs in here (lib/session-equipe.js registers itself when loaded):
 *   · `active()` — a user picked by PIN holds the session (their JWT has no refresh token);
 *   · `terminee(body)` — their session is over (SESSION_EQUIPE_TERMINEE, an expired JWT): back to the
 *     picker, never `/auth/refresh`;
 *   · `preuve()` — a 403 PREUVE_SESSION_EQUIPE_REQUISE: the owner re-types their password
 *     (`POST /auth/equipe/preuve`), resolves once proved, rejects when cancelled.
 * Null (a password session, or nothing registered): the historical behaviour, byte for byte.
 */
let sessionEquipe = null;
export function brancherSessionEquipe(gestion) {
  sessionEquipe = gestion ?? null;
}
export const CODE_PREUVE_SESSION_EQUIPE = CODES_SESSION_EQUIPE.PREUVE_SESSION_EQUIPE_REQUISE;

/**
 * The response-error interceptor, exported for unit tests (node env — no
 * real axios pipeline needed: it only reads `err.config` / `err.response`).
 */
export async function onResponseError(err) {
  const originalRequest = err.config;

  // 403 FORBIDDEN with a module code → the pharmacy hasn't subscribed to
  // this paid module. Surface a single toast; let the page-level
  // <ModuleGate> handle the empty state.
  if (err.response?.status === 403) {
    const body = err.response.data;
    if (body?.code === "FORBIDDEN" && body?.details?.module) {
      emitModuleForbiddenToast(body.details.module);
    }
  }

  // 401 on an auth endpoint (bad credentials / dead refresh token being
  // exchanged / reset-token misuse) → propagate to the caller's catch —
  // never a refresh attempt, never a redirect (R3-H27).
  if (
    err.response?.status === 401 &&
    (isAuthPassthroughRequest(originalRequest?.url) ||
      isCredentialOutcome(err.response?.data))
  ) {
    return Promise.reject(err.response?.data ?? err);
  }

  // X XWP-72 — a user picked by PIN on a team workstation: no refresh token exists for them.
  if (sessionEquipe && (originalRequest?._selectionEquipe || sessionEquipe.active?.())) {
    const body = err.response?.data;
    if (err.response?.status === 401) {
      sessionEquipe.terminee(body);
      return Promise.reject(body ?? err);
    }
    if (err.response?.status === 403 && body?.code === CODE_PREUVE_SESSION_EQUIPE && originalRequest && !originalRequest._preuveEquipe) {
      originalRequest._preuveEquipe = true;
      try {
        await sessionEquipe.preuve();
      } catch {
        return Promise.reject(body);
      }
      return apiClient(originalRequest);
    }
  }

  // If it's a 401 and we haven't already retried this request
  if (err.response?.status === 401 && !originalRequest._retry) {
    if (isRefreshing) {
      // If already refreshing, wait for it to finish and then retry this request
      try {
        const token = await new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        });
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return apiClient(originalRequest);
      } catch (err) {
        return Promise.reject(err);
      }
    }

    originalRequest._retry = true;
    isRefreshing = true;

    const refreshToken = localStorage.getItem("pharmakon_refresh");

    if (refreshToken) {
      try {
        // Attempt refresh using a clean axios instance to avoid infinite interceptor loops
        const res = await axios.post(`${API_BASE_URL}/auth/refresh`, { refresh_token: refreshToken });
        const { access_token, refresh_token: new_refresh_token } = res.data.data;

        localStorage.setItem("pharmakon_token", access_token);
        localStorage.setItem("pharmakon_refresh", new_refresh_token);

        apiClient.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
        originalRequest.headers.Authorization = `Bearer ${access_token}`;

        processQueue(null, access_token);
        return apiClient(originalRequest);
      } catch (_refreshError) {
        processQueue(_refreshError, null);
        // Refresh failed (e.g. refresh token expired)
        localStorage.removeItem("pharmakon_token");
        localStorage.removeItem("pharmakon_refresh");
        localStorage.removeItem("pharmakon_user");
        window.location.href = "/login";
        return Promise.reject(_refreshError);
      } finally {
        isRefreshing = false;
      }
    } else {
      // No refresh token available, just kick to login (and release the refresh latch: a request
      // that fails before the navigation completes must not wait on a refresh that never runs)
      isRefreshing = false;
      localStorage.removeItem("pharmakon_token");
      localStorage.removeItem("pharmakon_user");
      window.location.href = "/login";
      return Promise.reject(err);
    }
  }

  return Promise.reject(err.response?.data ?? err);
}

apiClient.interceptors.response.use((res) => res.data, onResponseError);

export default apiClient;

